import React, { useState }  from 'react';
import { useNavigate }       from 'react-router-dom';
import { Product }           from '../types';
import { aiSearch }          from '../utils/api';
import { AI_SEARCH_SUGGESTIONS } from '../utils/constants';
import ProductCard           from './ProductCard';
import './AISearchBar.css';

const AISearchBar: React.FC = () => {
  const [query,    setQuery]    = useState<string>('');
  const [results,  setResults]  = useState<Product[] | null>(null);
  const [loading,  setLoading]  = useState<boolean>(false);
  const [searched, setSearched] = useState<string>('');
  const [error,    setError]    = useState<string | null>(null);
  const navigate = useNavigate();

  const search = async (q?: string): Promise<void> => {
    const searchQuery = q ?? query;
    if (!searchQuery.trim()) return;
    setLoading(true);
    setSearched(searchQuery);
    setError(null);
    try {
      const data = await aiSearch(searchQuery);
      setResults(data.products ?? []);
    } catch (err: unknown) {
      const isTimeout = err instanceof Error && err.message.toLowerCase().includes('timeout');
      setError(isTimeout
        ? 'Search timed out — the server is slow. Please try again.'
        : 'Search failed. Please check your connection and try again.'
      );
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') search();
  };

  const clear = (): void => { setResults(null); setQuery(''); setError(null); };

  return (
    <div className="ai-searchbar">
      <div className="search-input-wrap">
        <span className="search-icon">🔍</span>
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Describe what you're looking for..."
          className="search-input"
        />
        <button className="btn-primary search-btn" onClick={() => search()} disabled={loading}>
          {loading ? '⏳' : 'Search'}
        </button>
      </div>

      {!results && !loading && (
        <div className="search-suggestions">
          {AI_SEARCH_SUGGESTIONS.map(s => (
            <button key={s} className="suggestion-pill"
              onClick={() => { setQuery(s); search(s); }}>{s}</button>
          ))}
        </div>
      )}

      {results !== null && (
        <div className="search-results">
          <div className="results-header">
            <span>🤖 AI found results for "<i>{searched}</i>"</span>
            <button onClick={clear} className="clear-btn">✕ Clear</button>
          </div>
          {error ? (
            <p className="no-results" style={{ color: 'var(--error)' }} role="alert">
              ⚠️ {error}
            </p>
          ) : results.length === 0 ? (
            <p className="no-results">
              No products found for this search. Try different words or{' '}
              <button onClick={() => navigate('/products')} className="link-btn">browse all</button>.
            </p>
          ) : (
            <div className="products-grid" style={{ marginTop: '16px' }}>
              {results.slice(0, 8).map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
          {!error && results.length > 8 && (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button className="btn-primary"
                onClick={() => navigate(`/products?search=${encodeURIComponent(searched)}`)}>
                View All Results →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AISearchBar;
